import { useState, useEffect } from 'react';
import { CheckCircle, Clock, Mail, MapPin, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import type { ContactInfo, LocationData, SiteVisit } from '../types';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { EmailService } from '../services/emailService';

interface ConfirmationScreenProps {
  contactInfo: ContactInfo | null;
  locationData?: LocationData;
  onViewDashboard: () => void;
  onSiteVisitScheduled: (visit: SiteVisit) => Promise<boolean>;
  existingProjectId?: string;
}

export function ConfirmationScreen({ 
  contactInfo, 
  locationData, 
  onViewDashboard, 
  onSiteVisitScheduled,
  existingProjectId 
}: ConfirmationScreenProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<Array<{ date: string; slots: string[] }>>([]);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState<string>('');

  const { scheduleSiteVisit } = useProjectStore();
  const { user } = useAuthStore();

  useEffect(() => {
    setAvailableDates(generateAvailableDates());
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + 7);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const availableSlots = [];
      const possibleSlots = ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];
      
      const numSlots = Math.floor(Math.random() * 3) + 3;
      for (let j = 0; j < numSlots; j++) {
        const randomIndex = Math.floor(Math.random() * possibleSlots.length);
        availableSlots.push(possibleSlots[randomIndex]);
        possibleSlots.splice(randomIndex, 1);
      }
      
      availableSlots.sort((a, b) => {
        const timeA = new Date(`01/01/2024 ${a}`);
        const timeB = new Date(`01/01/2024 ${b}`);
        return timeA.getTime() - timeB.getTime();
      });

      dates.push({
        date: date.toISOString().split('T')[0],
        slots: availableSlots
      });
    }

    return dates;
  };

  const handleScheduleVisit = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select both a date and time for your visit');
      return;
    }

    if (!user) {
      setError('Please sign in to schedule a site visit');
      return;
    }

    if (!existingProjectId) {
      setError('Project not found. Please try creating a new project');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const siteVisit: SiteVisit = {
        date: selectedDate,
        time: selectedTime,
        status: 'scheduled'
      };

      let success = false;
      
      if (existingProjectId) {
        success = await scheduleSiteVisit(existingProjectId, selectedDate, selectedTime);
      } else {
        success = await onSiteVisitScheduled(siteVisit);
      }
      
      if (!success) {
        throw new Error('Unable to schedule the site visit. Please try again later.');
      }

      setIsScheduled(true);
      setCountdown(60);

      // Send emails after successful site visit scheduling
      if (existingProjectId && user) {
        try {
          // Send emails (non-blocking - don't await)
          EmailService.sendProjectLeadEmail(existingProjectId).catch(err => 
            console.error('Lead email failed:', err)
          );
          EmailService.sendCustomerConfirmationEmail(existingProjectId).catch(err => 
            console.error('Confirmation email failed:', err)
          );
        } catch (error) {
          console.error('Email sending failed:', error);
          // Don't fail the operation if email fails
        }
      }
    } catch (err) {
      console.error('Error scheduling visit:', err);
      setError(
        err instanceof Error && err.message 
          ? err.message 
          : 'An unexpected error occurred. Please try again later.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomDateSelect = (date: string) => {
    const selectedDateTime = new Date(date).getTime();
    const minDateTime = new Date();
    minDateTime.setDate(minDateTime.getDate() + 7);
    
    if (selectedDateTime < minDateTime.getTime()) {
      setError('Please select a date at least 7 days from today');
      return;
    }

    setSelectedDate(date);
    setSelectedTime(null);
    setShowCustomDate(false);
    setError(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          <h2 className="text-3xl font-bold mb-4 text-gray-800">
            Thank You for Choosing Solar Energy!
          </h2>
          
          <p className="text-xl text-gray-600 mb-8">
            {existingProjectId 
              ? "Your solar project is ready for scheduling." 
              : "Your solar design request has been successfully submitted."}
          </p>
        </div>

        {locationData && (
          <div className="bg-blue-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <MapPin className="h-5 w-5 text-blue-600 mr-2" />
              Installation Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium text-gray-900">{locationData.address}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Area</p>
                <p className="font-medium text-gray-900">
                  {locationData.areaInSqMeters != null ? `${locationData.areaInSqMeters} m^2` : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Surface Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {locationData.surfaceType ?? 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Mounting Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {locationData.mountingType ? locationData.mountingType.replace(/-/g, ' ') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold mb-6 flex items-center">
            <Clock className="h-5 w-5 text-purple-600 mr-2" />
            Schedule Site Visit
          </h3>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {!isScheduled ? (
            <>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-medium text-gray-700">Select Date</h4>
                  <button
                    onClick={() => {
                      setShowCustomDate(!showCustomDate);
                      setError(null);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    {showCustomDate ? 'View Available Dates' : 'Choose Different Date'}
                  </button>
                </div>

                {showCustomDate ? (
                  <div className="bg-white p-4 rounded-lg border mb-4">
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => {
                        setCustomDate(e.target.value);
                        setError(null);
                      }}
                      min={getMinDate()}
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleCustomDateSelect(customDate)}
                      disabled={!customDate}
                      className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Check Availability
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {availableDates.map((day) => (
                      <button
                        key={day.date}
                        onClick={() => {
                          setSelectedDate(day.date);
                          setError(null);
                        }}
                        className={`p-3 rounded-lg text-center transition-all ${
                          selectedDate === day.date
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border hover:border-blue-500'
                        }`}
                      >
                        <CalendarIcon className="h-5 w-5 mx-auto mb-1" />
                        <p className="text-sm">{formatDate(day.date)}</p>
                        <p className="text-xs mt-1 opacity-75">{day.slots.length} slots available</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedDate && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Select Time</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableDates
                      .find(day => day.date === selectedDate)
                      ?.slots.map((time, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedTime(time);
                            setError(null);
                          }}
                          className={`p-3 rounded-lg text-center transition-all ${
                            selectedTime === time
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border hover:border-blue-500'
                          }`}
                        >
                          <Clock className="h-4 w-4 inline-block mr-2" />
                          {time}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleScheduleVisit}
                disabled={!selectedDate || !selectedTime || isLoading || !user || !existingProjectId}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? 'Scheduling...' : 'Schedule Visit'}
              </button>
            </>
          ) : (
            <div className="bg-green-50 rounded-lg p-4 text-green-800">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <p className="font-medium">Site Visit Scheduled!</p>
              </div>
              <p>Your site visit is scheduled for {formatDate(selectedDate!)} at {selectedTime}</p>
            </div>
          )}
        </div>

        {contactInfo && (
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Mail className="h-5 w-5 text-blue-600 mr-2" />
              Your Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium text-gray-900">{contactInfo.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900">{contactInfo.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-gray-900">{contactInfo.phone}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={onViewDashboard}
            disabled={!isScheduled}
            className={`bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium shadow-lg ${
              !isScheduled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isScheduled ? 'View Dashboard' : 'Schedule a Visit to Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
